import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import {
  EVALUATIONS_SUMMARY_QUERY_KEY,
  TEACHER_MESSAGES_QUERY_KEY,
} from '@/api/queryKeys';
import { AbsencesTile } from '@/components/Dashboard/AbsencesTile';
import { DiaryTile } from '@/components/Dashboard/DiaryTile';
import { GradesTile } from '@/components/Dashboard/GradesTile';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useResetUnseenEvents } from '@/hooks/useResetUnseenEvents';
import { DashboardTileType, UnseenEventType } from '@/types/components';

const ALL_TILES = [
  DashboardTileType.Grades,
  DashboardTileType.Diary,
  DashboardTileType.Absences,
];

const tileUnseenEvents: Record<
  DashboardTileType,
  { eventTypes: UnseenEventType[]; queryKey: readonly string[] }
> = {
  [DashboardTileType.Grades]: {
    eventTypes: [UnseenEventType.Evaluation],
    queryKey: EVALUATIONS_SUMMARY_QUERY_KEY,
  },
  [DashboardTileType.Diary]: {
    eventTypes: [UnseenEventType.BehaviorEntry, UnseenEventType.DiaryNote],
    queryKey: TEACHER_MESSAGES_QUERY_KEY,
  },
  [DashboardTileType.Absences]: {
    eventTypes: [UnseenEventType.NonAttendance],
    queryKey: EVALUATIONS_SUMMARY_QUERY_KEY,
  },
};

export const Dashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const hasSubscription = useHasFamilySubscription();
  const resetUnseenEvents = useResetUnseenEvents();

  const handleMobileTileToggle = async (openedTile: string) => {
    if (!hasSubscription || !openedTile) {
      return;
    }

    const { eventTypes, queryKey } =
      tileUnseenEvents[openedTile as DashboardTileType];

    await resetUnseenEvents(eventTypes);
    await queryClient.invalidateQueries({ queryKey });
  };

  return (
    <div className="px-4 pt-3 md:px-6 md:pt-6">
      <AccordionPrimitive.Root
        type="multiple"
        defaultValue={ALL_TILES}
        className="hidden gap-5 md:grid md:auto-rows-[390px] md:grid-cols-3 md:has-[[data-tile-footer=grades],[data-tile-footer=diary]]:auto-rows-[424px]"
      >
        <GradesTile />
        <DiaryTile />
        <AbsencesTile />
      </AccordionPrimitive.Root>
      <AccordionPrimitive.Root
        type="single"
        collapsible
        className="flex flex-col gap-3 md:hidden"
        onValueChange={handleMobileTileToggle}
      >
        <GradesTile />
        <DiaryTile />
        <AbsencesTile />
      </AccordionPrimitive.Root>
    </div>
  );
};
